const loaders = new WeakMap();
const campos = {
  total: "TOTAL", atendimentos: "DETALHE", documentos: "DOCUMENTO",
  medicacoes: "MEDICAMENTO", receituarios: "RECEITUARIO", imagens: "IMAGEM",
  cid: "CID", internacoes: "INTERNACAO", consultas: "CONSULTA", exames: "EXAME",
  cirurgias: "CIRURGIA", solicitacoes: "SOLICITACAO", agendasRetorno: "AGENDA_RETORNO"
};

export function quantidadesHistorico(datas) {
  const totais = { visaoGeral: datas.length };
  for(const [campo, origem] of Object.entries(campos)) {
    totais[campo] = datas.reduce((total, data) => total + (parseInt(data[origem], 10) || 0), 0);
  }
  return totais;
}

// Leituras apenas: falhas HTTP não viram respostas vazias nem disparam retries ocultos.
export async function consultarHistorico(api, jwt, recurso, parametros) {
  if(!jwt.getToken()) { throw new Error("Sessão indisponível para consultar o histórico."); }
  const { signal, ...filtros } = parametros;
  api.setHeader();
  const { data } = await api.postOnce(`atendimentos/access/${recurso}`, filtros, { signal, timeout: 120000 });
  const lista = recurso === "listaPEPEstatisticas" ? data?.ESTATISTICAS : data;
  if(!Array.isArray(lista)) { throw new Error("Resposta inválida ao consultar o histórico."); }
  if(recurso !== "listaPEPEstatisticas" && lista.some(item => !item || typeof item.DATA_INCLUSAO !== "string" || !/^\d{4}-\d{2}-\d{2}/.test(item.DATA_INCLUSAO))) {
    throw new Error("Data inválida na resposta do histórico.");
  }
  return data;
}

export function historicoLoader(vm) {
  if(!loaders.has(vm)) { loaders.set(vm, criarHistoricoLoader(vm)); }
  return loaders.get(vm);
}

export function criarHistoricoLoader(vm) {
  let contexto = null;
  let encerrado = false;
  const atual = ctx => !encerrado && contexto === ctx && String(vm.idPaciente) === ctx.paciente && vm.$route.fullPath === ctx.rota;
  const aviso = "Não foi possível carregar todo o histórico. Os dados já carregados foram mantidos. Tente novamente.";
  const atualizarAviso = ctx => {
    if(atual(ctx)) {
      vm.erroHistorico = ctx.datesFailed ? "Não foi possível atualizar as datas do histórico. Tente novamente." :
        (ctx.rows.some(row => row.erroCarregamento) ? aviso : "");
      vm.historicoDiasCarregados = ctx.rows.filter(row => !row.historicoPendente && !row.isSearching && !row.erroCarregamento).length;
      vm.historicoTotalDias = ctx.rows.length;
      vm.historicoParcial = ctx.datesFailed || vm.historicoDiasCarregados < ctx.rows.length;
    }
  };
  function parar() {
    if(contexto) { contexto.controller.abort(); }
    contexto = null;
    vm.isLoading = false;
    vm.carregandoTimeLine = false;
  }
  function novoDia(data, total = 0) {
    return { DATA: data, TOTAL: total, ITEMS: [], collapsed: vm.timelinePEPEntraAgrupada,
      isLoading: false, isSearching: false, historicoPendente: true, solicitado: false, erroCarregamento: false, versaoHistorico: 0 };
  }
  function executar(ctx) {
    if(!atual(ctx)) { return Promise.resolve(); }
    if(ctx.worker) { return ctx.worker; }
    vm.carregandoTimeLine = true;
    // Deferir o início permite registrar a promise antes de concluir um histórico vazio.
    ctx.worker = Promise.resolve().then(async () => {
      while(atual(ctx)) {
        const lote = ctx.rows.filter(row => row.solicitado && row.historicoPendente && !row.erroCarregamento).slice(0, 5);
        if(!lote.length) { break; }
        const versoes = lote.map(row => row.versaoHistorico);
        lote.forEach(row => { row.historicoPendente = false; row.isSearching = true; row.isLoading = true; });
        try {
          const items = await vm.buscarTimeLineData(lote.map(row => row.DATA), ctx.paciente, ctx.controller.signal);
          if(!atual(ctx)) { return; }
          // Preparar todos os grupos antes de substituir qualquer dia.
          const grupos = new Map(lote.map(row => [row.DATA, []]));
          for(const item of items) {
            const dia = item.DATA_INCLUSAO.slice(0, 10);
            if(grupos.has(dia)) { grupos.get(dia).push(item); }
          }
          lote.forEach((row, i) => {
            if(row.versaoHistorico === versoes[i]) {
              row.ITEMS = grupos.get(row.DATA);
              row.erroCarregamento = false;
            }
          });
        } catch(error) {
          if(!atual(ctx)) { return; }
          lote.forEach(row => { row.erroCarregamento = true; });
          break;
        } finally {
          if(atual(ctx)) { lote.forEach(row => { row.isSearching = false; row.isLoading = false; }); atualizarAviso(ctx); }
        }
      }
    }).finally(() => {
      ctx.worker = null;
      if(atual(ctx)) {
        ctx.rows.forEach(row => { row.isLoading = false; row.isSearching = false; });
        vm.carregandoTimeLine = false;
        atualizarAviso(ctx);
      }
    });
    return ctx.worker;
  }
  function carregar(forcar = false, completo = false) {
    if(encerrado) { return Promise.resolve(); }
    if(contexto && atual(contexto) && contexto.refresh && !forcar) { return contexto.refresh; }
    const mesmoPaciente = contexto && contexto.paciente === String(vm.idPaciente);
    parar();
    const ctx = { paciente: String(vm.idPaciente), rota: vm.$route.fullPath,
      controller: new AbortController(), rows: [], datesFailed: false, worker: null, refresh: null };
    contexto = ctx;
    if(!mesmoPaciente) {
      vm.atendimentos = [];
      vm.quantidadesAtendimentos = quantidadesHistorico([]);
    }
    vm.atendimentos.forEach(row => { row.isLoading = false; row.isSearching = false; });
    vm.isLoading = true;
    vm.erroHistorico = "";
    ctx.refresh = Promise.resolve().then(async () => {
      try {
        const datas = await vm.getPEPDatas({ ID_PACIENTE: ctx.paciente, signal: ctx.controller.signal });
        if(!atual(ctx)) { return; }
        vm.quantidadesAtendimentos = quantidadesHistorico(datas);
        vm.atendimentos = datas.map(data => novoDia(data.DATA_INCLUSAO.slice(0, 10), data.TOTAL)).sort((a, b) => b.DATA.localeCompare(a.DATA));
        ctx.rows = vm.atendimentos;
        ctx.rows.slice(0, completo ? ctx.rows.length : (vm.quantidadeDatasExibindo || 10)).forEach(row => { row.solicitado = true; });
        vm.avisoCompletamenteCarregado = false;
        vm.isLoading = false;
        await executar(ctx);
      } catch(error) {
        if(atual(ctx)) {
          ctx.datesFailed = true;
          vm.erroHistorico = "Não foi possível atualizar as datas do histórico. Tente novamente.";
        }
      } finally {
        ctx.refresh = null;
        if(atual(ctx)) { vm.isLoading = false; }
      }
    });
    return ctx.refresh;
  }
  function tentarNovamente() {
    const ctx = contexto;
    if(!ctx || !atual(ctx) || ctx.datesFailed) { return carregar(true); }
    if(ctx.worker || ctx.refresh) { return ctx.worker || ctx.refresh; }
    ctx.rows.forEach(row => { if(row.erroCarregamento) { row.historicoPendente = true; row.erroCarregamento = false; } });
    return executar(ctx);
  }
  async function pronto() {
    if(encerrado) { return null; }
    if(!contexto || !atual(contexto)) { carregar(); }
    const ctx = contexto;
    // As datas já disponíveis permitem invalidar um lote em andamento sem esperar sua resposta.
    if(vm.isLoading && ctx.refresh) { await ctx.refresh; }
    return atual(ctx) && !ctx.datesFailed ? ctx : null;
  }
  const confirmado = row => !row.historicoPendente && !row.isSearching && !row.erroCarregamento;
  async function solicitar(selecionar) {
    const ctx = await pronto();
    if(!ctx) { return false; }
    const rows = selecionar(ctx.rows);
    rows.forEach(row => { row.solicitado = true; });
    await executar(ctx);
    return atual(ctx) && rows.every(confirmado);
  }
  function completar() { return solicitar(rows => rows); }
  function garantirQuantidade(quantidade) { return solicitar(rows => rows.slice(0, quantidade)); }
  function garantirDatas(datas) { return solicitar(rows => rows.filter(row => datas.includes(row.DATA))); }
  async function recarregarDias(datas) {
    const ctx = await pronto();
    if(!ctx) { return false; }
    const rows = [...new Set(datas)].map(data => {
      let row = ctx.rows.find(item => item.DATA === data);
      if(!row) { ctx.rows.push(novoDia(data)); row = ctx.rows[ctx.rows.length - 1]; }
      row.versaoHistorico++;
      row.solicitado = true; row.historicoPendente = true; row.erroCarregamento = false;
      return row;
    });
    ctx.rows.sort((a, b) => b.DATA.localeCompare(a.DATA));
    await executar(ctx);
    return atual(ctx) && rows.every(confirmado);
  }
  return { carregar, tentarNovamente, completar, garantirQuantidade, garantirDatas, recarregarDias,
    recarregarDia: data => recarregarDias([data]),
    encerrar() { encerrado = true; parar(); } };
}
